import { Auth, signInWithEmailAndPassword } from "firebase/auth";

/**
 * Dev/test-only auth seam.
 *
 * Establishes a known test-user session at app start so an automated/agent-driven verify loop can
 * exercise auth-gated flows (e.g. the phone-verification gate) WITHOUT anyone typing credentials
 * into the login UI. The credentials live in a **gitignored, human-created** file that the *app*
 * reads at runtime — the agent never sees or enters them.
 *
 * Safety:
 *  - HARD-GATED to non-production: returns immediately when `REACT_APP_PRODUCTION === 'true'`, so
 *    production web/mobile builds can never auto-sign-in.
 *  - Opt-in per run: inert unless `public/test-auth.local.json` exists AND has `"enabled": true`.
 *    That file is gitignored (see `.gitignore`) and only ever created locally by a developer.
 *  - No-op if a real session is already present (won't clobber a manual login).
 *
 * Setup (see `public/test-auth.local.json.example`):
 *  1. A human creates a throwaway test account through the normal sign-up flow.
 *  2. They copy the example to `public/test-auth.local.json` and fill in its email + password.
 *  3. Run the app non-prod (`npm start`) → it comes up signed in as that user.
 */
export const maybeSignInTestUser = async (auth: Auth): Promise<void> => {
    // 1. Never in production.
    if (process.env.REACT_APP_PRODUCTION === "true") return;
    // 2. Don't clobber an existing (e.g. manually established) session.
    if (auth.currentUser) return;

    try {
        // 3. Opt-in via a gitignored local config the app fetches at runtime.
        const res = await fetch("/test-auth.local.json", { cache: "no-store" });
        if (!res.ok) return; // absent → seam is inert
        const cfg = await res.json();
        if (!cfg || cfg.enabled !== true || !cfg.email || !cfg.password) return;

        console.warn(
            "[dev-test-auth] Signing in test user from public/test-auth.local.json (non-prod, opt-in).",
        );
        await signInWithEmailAndPassword(auth, cfg.email, cfg.password);
    } catch (e) {
        // Never block app startup on the seam.
        console.warn("[dev-test-auth] Test-user sign-in skipped:", e);
    }
};
